import type { ApolloError } from '@apollo/client';
import { useGetFeatureSetQuery } from '@graphql/queries/featureSet.graphql.generated';
import { useCallback, useState, useMemo } from 'react';
import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import type { FeatureSet, FeatureSetType } from './featureSet.types';
import { deriveFeatureSet } from './deriveFeatureSet';

/**
 * useFeatureSet retrieves which FeatureSet areas (Website, Registry, Rsvp) are enabled for the current event.
 *
 * Returns:
 * - isInitialized: true once the query has resolved with an event.
 * - features: FeatureSet map, or undefined while loading or when the event is missing.
 * - hasFeatureSetEnable: helper to check a single feature's enabled state.
 * - hasErrors: the last ApolloError, if any.
 * - refetchFeatureSet: re-runs the underlying query.
 */

export const useFeatureSet = () => {
  const eventNameMatch = useEventNameMatch();
  const [hasErrors, setHasErrors] = useState<Maybe<ApolloError>>(null);

  const { loading, refetch, data } = useGetFeatureSetQuery({
    batchMode: 'off',
    ssr: true,
    variables: {
      eventName: eventNameMatch.eventName || '',
    },
    onError(err) {
      setHasErrors(err);
    },
  });

  const featureSetOpts: FeatureSet | undefined = useMemo(() => {
    if (loading) {
      return undefined;
    }

    // Bail out if data is missing (e.g., query failed or returned no data)
    // This prevents undefined values from being treated as "feature enabled"
    if (!data?.eventByName) {
      return undefined;
    }

    return deriveFeatureSet(data.eventByName);
  }, [data, loading]);

  const hasFeatureSetEnable = useCallback(
    (feature: FeatureSetType) => {
      return featureSetOpts ? !!featureSetOpts[feature] : false;
    },
    [featureSetOpts],
  );

  return {
    isInitialized: !loading && !!data?.eventByName,
    features: featureSetOpts,
    hasFeatureSetEnable,
    hasErrors,
    refetchFeatureSet: refetch,
  };
};
